
import { motion, AnimatePresence } from 'framer-motion';
import { fadeUp, textReveal } from '@/animations/variants';
import Button from '@/components/common/Button';
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import GradientText from '@/components/Text/GradientText';
import LiquidEther from '@/components/Background.tsx/LiquidEther';

interface HeroSectionProps {
  onStartConversation?: () => void;
}

export default function HeroSection({ onStartConversation }: HeroSectionProps) {
  const navigate = useNavigate();
  const sentences = [
    "YOU!",
    "Unleash the AI",
    "Harness the Data",
    "Cognitive Power"
  ];
  const simpleTexts = [
    "Ennobridge is All about",
    "Connect Ennobridge to",
    "Trust Ennobridge to",
    "Employ Ennobridge to"
  ];

  const [current, setCurrent] = useState(0);

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrent((prev) => (prev + 1) % sentences.length);
    }, 4000); // Change every 4 seconds
    return () => clearInterval(interval);
  }, []);

  return (
    <section className="relative min-h-fit sm:min-h-[50vh] lg:min-h-screen flex items-center bg-gradient-to-br from-background via-surface to-background overflow-hidden pt-6 pb-8 sm:py-8 lg:py-0">
      {/* LiquidEther background for the entire section */}
      <LiquidEther
        className="pointer-events-none"
        style={{ zIndex: 0, position: 'absolute', top: 0, left: 0, width: '100%', height: '100%' }}
      />
      {/* Background Pattern */}
      <div className="absolute inset-0 opacity-20" style={{ zIndex: 1 }}>
        <div className="absolute inset-0" style={{
          backgroundImage: 'radial-gradient(circle at 25px 25px, rgba(22, 38, 96, 0.1) 2px, transparent 0)',
          backgroundSize: '50px 50px',
        }} />
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <div className="flex flex-col items-center justify-center text-center w-full">
          {/* Centered Content */}
          <motion.div
            className="w-full max-w-3xl mx-auto relative"
            initial="hidden"
            animate="visible"
            variants={{
              hidden: {},
              visible: {
                transition: {
                  staggerChildren: 0.2,
                },
              },
            }}
          >
            <div className="relative z-10 flex flex-col items-center justify-center">
              <motion.div variants={textReveal} className="flex flex-col items-center justify-center w-full">
                {/* Simple text above the animated sentence */}
                <AnimatePresence mode="wait">
                  <motion.div
                    key={"simple-" + current}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.4, ease: 'easeInOut' }}
                    className="text-lg sm:text-xl md:text-2xl lg:text-3xl xl:text-4xl font-medium text-[#162660] mb-4"
                    style={{ minHeight: 0, marginBottom: '1.5rem' }}
                  >
                    {simpleTexts[current]}
                  </motion.div>
                </AnimatePresence>
                <h1
                  className="text-3xl sm:text-4xl md:text-5xl lg:text-5xl xl:text-6xl font-heading font-bold text-textPrimary leading-tight min-h-0"
                  style={{ marginTop: 0 }}
                >
                  <AnimatePresence mode="wait">
                    <motion.div
                      key={current}
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      exit={{ opacity: 0 }}
                      transition={{ duration: 0.4, ease: 'easeInOut' }}
                      style={{ display: 'inline-block', width: '100%' }}
                    >
                      <GradientText
                        className="inline-block font-bold zenitha-font"
                        colors={['#FF00FF', '#4B0082', '#C8005A']}
                        animationSpeed={4}
                        direction="horizontal"
                        yoyo={true}
                      >
                        {sentences[current]}
                      </GradientText>
                    </motion.div>
                  </AnimatePresence>
                </h1>
              </motion.div>

              <motion.p 
                className="text-base sm:text-lg md:text-xl lg:text-xl xl:text-2xl mb-4 sm:mb-6 lg:mb-8 max-w-2xl mx-auto lg:mx-0"
                style={{ color: '#162660' }}
                variants={textReveal}
              >
                Partner with Ennobridge to transform your ideas into scalable platforms with highly available 
                residential engineering teams, vast expert consultants network, and AI-augmented workforce.
              </motion.p>

              <motion.div 
                className="flex flex-col sm:flex-row gap-3 sm:gap-4 mb-4 sm:mb-6 lg:mb-8 justify-center"
                variants={fadeUp}
              >
                <Button 
                  size="lg" 
                  className="w-full sm:w-auto"
                  onClick={onStartConversation || (() => navigate('/contact#contact-methods'))}
                >
                  Start a Conversation
                </Button>
                <Button
                  size="lg"
                  variant="outline"
                  className="w-full sm:w-auto"
                  onClick={() => {
                    navigate('/services');
                    window.scrollTo({ top: 0, behavior: 'smooth' });
                  }}
                >
                  Explore Our Services
                </Button>
              </motion.div>

              {/* Key Stats */}
              <motion.div 
                className="flex flex-wrap justify-center gap-4 sm:gap-6 lg:gap-8"
                variants={fadeUp}
              >
                <div className="text-center">
                  <div className="text-xl sm:text-2xl font-accent font-bold text-primary">4+</div>
                  <div className="text-xs sm:text-sm text-textSecondary">Years</div>
                </div>
                <div className="text-center">
                  <div className="text-xl sm:text-2xl font-accent font-bold text-primary">Global</div>
                  <div className="text-xs sm:text-sm text-textSecondary">Operations</div>
                </div>
                <div className="text-center">
                  <div className="text-xl sm:text-2xl font-accent font-bold text-primary">24×7</div>
                  <div className="text-xs sm:text-sm text-textSecondary">Availability</div>
                </div>
                <div className="text-center">
                  <div className="text-xl sm:text-2xl font-accent font-bold text-primary">Immediate</div>
                  <div className="text-xs sm:text-sm text-textSecondary">Resource Availability</div>
                </div>
                <div className="text-center">
                  <div className="text-xl sm:text-2xl font-accent font-bold text-primary">1000+</div>
                  <div className="text-xs sm:text-sm text-textSecondary">Consultant Network</div>
                </div>
              </motion.div>
            </div>
          </motion.div>

          {/* Right Column - Visual (Removed for centering) */}
        </div>
      </div>
    </section>
  );
}